/**
 * Rattachement MANUEL challenge -> funded (previousAccountId).
 *
 * POURQUOI CE MODULE EXISTE
 * Le rattachement automatique du cycle de sync (fundedTransition.ts) ne se
 * déclenche plus en pratique : il exige les listes de trades REST pour prouver
 * qu'un compte découvert n'a jamais tradé, listes que le cycle ne lit plus en
 * `ws_only` (3B-2). Et même quand il tourne, il s'abstient dès que deux
 * challenges de même taille sont candidats, ce qui est le cas d'un trader qui
 * valide plusieurs comptes la même semaine. Résultat constaté : les PA arrivent
 * par l'écran d'import, sans lien, et le challenge reste affiché vivant.
 *
 * Ici c'est l'UTILISATEUR qui désigne le challenge, à l'import ou depuis la
 * fiche du compte funded. Le risque qui rend le rattachement automatique si
 * prudent (un lien faux, permanent et silencieux) disparaît : le lien est
 * choisi, visible, et peut être défait.
 *
 * Module PUR : aucun import Wasp/Prisma, testable isolément.
 */

import { CLOSURE_REASON } from "./accountClosureReason";

/** Un challenge désactivé depuis plus longtemps ne peut plus être proposé. */
export const PREDECESSOR_INACTIVE_WINDOW_DAYS = 60;

/** Tolérance de taille pour PRÉ-REMPLIR le choix (jamais pour décider seul). */
export const PREDECESSOR_SUGGEST_TOLERANCE = 0.01;

export interface PredecessorCandidateRow {
  id: string;
  name: string;
  status: string;
  isActive: boolean;
  createdAt: Date;
  disabledAt: Date | null;
  deactivatedReason: string | null;
  fundedAt: Date | null;
  targetReachedAt: Date | null;
  initialBalance: number;
  fundedStartingBalance: number | null;
  /** Taille du palier (PropFirmTier.accountSize), si connue. */
  tierSize: number | null;
  /** Template effectif : celui du compte, à défaut celui de son groupe. */
  propFirmTemplateId: string | null;
  /** Ce compte a-t-il DÉJÀ un successeur (un autre compte le référence) ? */
  hasSuccessor: boolean;
}

export interface PredecessorFilterContext {
  now: Date;
  /** Compte funded à rattacher (absent à l'import : il n'existe pas encore). */
  fundedAccountId?: string;
  /** Date de création du funded : un challenge lui est forcément antérieur. */
  fundedCreatedAt?: Date;
  /** Prop firm du funded. Null = inconnue, on ne filtre pas dessus. */
  propFirmTemplateId: string | null;
  /**
   * Lève la fenêtre de PREDECESSOR_INACTIVE_WINDOW_DAYS. Pour la réparation
   * depuis un compte EXISTANT (fiche, modale) : un PA importé il y a des mois
   * sans lien doit pouvoir retrouver son challenge, désactivé depuis aussi
   * longtemps. L'import garde la fenêtre (liste courte d'un PA neuf).
   */
  ignoreInactiveWindow?: boolean;
}

/**
 * Ce compte peut-il être désigné comme le challenge d'origine ?
 *
 * Un compte désactivé a perdu son statut (status = "inactive") : on ne sait
 * plus s'il était challenge ou funded. On l'accepte quand rien ne le contredit
 * (pas de fundedAt, pas cramé) et qu'il est récent, car l'utilisateur a pu
 * désactiver ses challenges à la main faute de lien, c'est précisément le cas
 * que la réparation depuis la fiche compte doit couvrir.
 */
export function isPredecessorCandidate(
  row: PredecessorCandidateRow,
  ctx: PredecessorFilterContext,
): boolean {
  if (row.id === ctx.fundedAccountId) return false;
  if (row.hasSuccessor) return false;
  if (ctx.fundedCreatedAt && row.createdAt >= ctx.fundedCreatedAt) return false;
  if (ctx.propFirmTemplateId && row.propFirmTemplateId !== ctx.propFirmTemplateId) return false;

  if (row.status === "challenge") return true;

  if (row.status !== "inactive") return false;
  // Un compte cramé n'a jamais produit de funded.
  if (row.deactivatedReason === CLOSURE_REASON.DRAWDOWN_BREACH) return false;
  // fundedAt = promu funded en place : c'était un funded, pas un challenge.
  if (row.fundedAt) return false;
  if (!row.disabledAt) return false;
  if (ctx.ignoreInactiveWindow) return true;
  const windowStart = ctx.now.getTime() - PREDECESSOR_INACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return row.disabledAt.getTime() >= windowStart;
}

/**
 * Ordre d'affichage : d'abord les challenges qui ont atteint leur objectif
 * (les plus probables), puis les challenges vivants, puis les désactivés. Le
 * plus récent en premier dans chaque groupe.
 */
export function rankPredecessorCandidates<T extends PredecessorCandidateRow>(rows: T[]): T[] {
  const rank = (r: T) => (r.targetReachedAt ? 0 : r.status === "challenge" ? 1 : 2);
  return [...rows].sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return b.createdAt.getTime() - a.createdAt.getTime();
  });
}

/** Taille attendue du funded issu de ce challenge. */
export function expectedFundedSize(row: PredecessorCandidateRow): number | null {
  const size = row.fundedStartingBalance ?? row.tierSize ?? row.initialBalance;
  return size > 0 ? size : null;
}

/**
 * Challenge à PRÉ-SÉLECTIONNER pour un funded de cette balance, ou null.
 *
 * Simple aide à la saisie : l'utilisateur voit le choix et le valide. On ne
 * pré-remplit que sur une correspondance UNIQUE, en préférant les challenges
 * dont l'objectif a été constaté. Deux 50k validés la même semaine ne sont
 * pas départageables par la taille : la liste reste vide plutôt que fausse.
 */
export function suggestPredecessor<T extends PredecessorCandidateRow>(
  fundedBalance: number | null,
  candidates: T[],
): T | null {
  if (fundedBalance === null || !(fundedBalance > 0)) return null;
  const sized = candidates.filter((c) => {
    const size = expectedFundedSize(c);
    return size !== null && Math.abs(fundedBalance - size) / size <= PREDECESSOR_SUGGEST_TOLERANCE;
  });
  const reached = sized.filter((c) => c.targetReachedAt);
  if (reached.length === 1) return reached[0];
  if (reached.length === 0 && sized.length === 1) return sized[0];
  return null;
}

/**
 * Un même challenge désigné pour deux funded du même import : refus. Rend les
 * ids en double, vide si tout est cohérent.
 */
export function duplicatePredecessorIds(ids: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const id of ids) {
    if (!id) continue;
    if (seen.has(id)) dup.add(id);
    seen.add(id);
  }
  return [...dup];
}

export interface ChallengeLinkState {
  status: string;
  isActive: boolean;
  disabledAt: Date | null;
  deactivatedReason: string | null;
  fundedPromptState: string | null;
}

/**
 * Ce que devient le challenge une fois son funded rattaché.
 *
 *  - encore vivant -> désactivé avec le motif `challenge_completed` (pastille
 *    « réussi », pas « cramé »). C'est ce que l'utilisateur faisait à la main ;
 *  - déjà désactivé sans motif, ou en `broker_account_gone` (disparition
 *    ambiguë) -> requalifié `challenge_completed`, le lien levant l'ambiguïté ;
 *  - le prompt « objectif atteint » encore en attente est considéré répondu :
 *    il ne doit plus réapparaître pour un challenge dont on connaît la suite.
 */
export function challengePatchOnLink(
  challenge: ChallengeLinkState,
  now: Date,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (challenge.isActive || challenge.status !== "inactive") {
    patch.status = "inactive";
    patch.isActive = false;
    patch.disabledAt = challenge.disabledAt ?? now;
    patch.deactivatedReason = CLOSURE_REASON.CHALLENGE_COMPLETED;
  } else if (
    challenge.deactivatedReason === null ||
    challenge.deactivatedReason === CLOSURE_REASON.BROKER_GONE
  ) {
    patch.deactivatedReason = CLOSURE_REASON.CHALLENGE_COMPLETED;
  }
  if (challenge.fundedPromptState === "pending") patch.fundedPromptState = "confirmed";
  return patch;
}

/**
 * Le bouton « Passer en Funded » (promotion EN PLACE : le compte lui-même
 * devient "pa") est-il permis pour ce compte ?
 *
 * Jamais pour un compte synchronisé par un broker : la prop firm délivre le
 * funded sous un AUTRE compte, que la sync importe à part. Promouvoir le
 * challenge en place produirait alors deux PA pour une seule réussite. Le
 * chemin correct est le lien depuis le PA (import ou fiche du compte).
 */
export function canPromoteInPlace(account: {
  status: string;
  tradovateSyncedAccount?: unknown;
  metaApiConnection?: unknown;
}): boolean {
  if (account.status !== "challenge") return false;
  return !account.tradovateSyncedAccount && !account.metaApiConnection;
}
